import { Component, ViewChild, computed, effect, inject, input, resource, signal, ChangeDetectionStrategy } from '@angular/core';
import { MailTemplate } from '../models/mail-template';
import { MailTemplatesService } from '../services/mail-templates.service';
import { FormsModule, NgForm } from '@angular/forms';
import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { HtmlEditorComponent } from 'src/app/controls/components/advanced/html-editor/html-editor.component';
import { ToggleButtonComponent, ToggleButtonItem } from 'src/app/controls/components/basic/toggle-button/toggle-button.component';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { FormMode, FormPageComponent } from 'src/app/controls/components/template/form-page/form-page.component';
import { PageCommandGroupComponent } from '../../controls/components/misc/page-command-group/page-command-group.component';
import { PageCommandComponent } from '../../controls/components/misc/page-command/page-command.component';
import { MacroDefinition } from '../models/macro-definition';
import { MacroObjectType } from '../models/macro-object-type';

@Component({
  selector: 'app-mail-templates-details',
  templateUrl: './mail-templates-details.component.html',
  styleUrl: './mail-templates-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, LayoutRowComponent, LayoutColumnComponent, TextboxComponent, ToggleButtonComponent, HtmlEditorComponent, FormPageComponent, PageCommandGroupComponent, PageCommandComponent]
})
export class MailTemplatesDetailsComponent {
  @ViewChild('tForm')
  private form: NgForm;
  private mailTemplatesService = inject(MailTemplatesService);
  private toastService = inject(ToastService);

  mailTemplateId = input<number>();
  mailTemplateKey = input<string>();

  macros = computed(() => this.macroResource.value() ?? []);
  mailTemplate = computed(() => this.mailTemplateResource.value());
  useCustom = computed(() => this.selectedButtons().includes('Custom'));
  selectedButtons = signal<string[]>([]);

  mode: FormMode = FormMode.View;

  buttons: ToggleButtonItem[] = [
    {
      key: 'Default',
      text: 'Default Mail',
      icon: '',
      hint: 'Use default mail template'
    },
    {
      key: 'Custom',
      text: 'Custom Mail',
      icon: '',
      hint: 'Use custom mail template'
    }
  ];

  macroResource = resource({
    params: () => ({macroObjectType: this.mailTemplate()?.MacroObjectType}),
    loader: ({params}) => this.getMacroDefinitions(params),
  });

  mailTemplateResource = resource({
    params: () => ({id: this.mailTemplateId(), key: this.mailTemplateKey()}),
    loader: ({params}) => this.getMailTemplate(params),
  });

  constructor() {
    effect(() => {
      this.selectedButtons.set(this.mailTemplate()?.UseCustom ? ['Custom'] : ['Default']);
    });
  }

  async getMacroDefinitions(request: {macroObjectType: MacroObjectType}): Promise<MacroDefinition[]> {
    if (request.macroObjectType) {
      return await this.mailTemplatesService.getMacroDefinitions(request.macroObjectType);
    }
    else {
      return null;
    }
  }

  async getMailTemplate(request: {id: number, key: string}): Promise<MailTemplate> {
    if (request.id) {
      return await this.mailTemplatesService.getMailTemplateById(request.id);
    }
    else if (request.key) {
      return await this.mailTemplatesService.getMailTemplateByKey(request.key);
    }
    else {
      return null;
    }
  }

  onUseCustomChange(e: string[]) {
    this.selectedButtons.set(e ?? ['Default']);
  }

  onSaveClick = async (): Promise<void> => {
    this.form?.form.markAllAsTouched();

    if (this.form?.invalid) {
      this.toastService.showToast('Please fill in required fields.', ToastType.error);
      this.form.reset(
        {
          CustomSubject: this.mailTemplate().CustomSubject,
          CustomBody: this.mailTemplate().CustomBody,
          DefaultSubject: this.mailTemplate().Subject,
          DefaultBody: this.mailTemplate().Body,
          DisplayName: this.mailTemplate().DisplayName,
          Description: this.mailTemplate().Description,
          custom: this.selectedButtons()
        });
      return;
    }

    const t = new MailTemplate({
      ...this.mailTemplate(),
      ...this.form.value
    } as Partial<MailTemplate>);
    t.UseCustom = this.selectedButtons().includes('Custom');

    try {
      const savedMailTemplate = await this.mailTemplatesService.saveMailTemplate(t);
      const savedTemplateStatus = savedMailTemplate.UseCustom ? 'Custom' : 'Default';
      this.toastService.showToast('Template set to ' + savedTemplateStatus, ToastType.success);
      this.mailTemplateResource.reload();
    }
    catch {
      this.toastService.showToast('Failed to save mail template.', ToastType.error);
    }
  }

  onCancelClick = (): void => {
    this.mailTemplateResource.reload();
    this.form.reset(
      {
        CustomSubject: this.mailTemplate().CustomSubject,
        CustomBody: this.mailTemplate().CustomBody,
        DefaultSubject: this.mailTemplate().Subject,
        DefaultBody: this.mailTemplate().Body,
        DisplayName: this.mailTemplate().DisplayName,
        Description: this.mailTemplate().Description,
        custom: this.selectedButtons()
      });
  }

  onModeChanged = (e: FormMode): void => {
    console.log('Mode changed to ' + e);
  }
}
